import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { T } from './theme';

// Атмосфера «cosmic command deck»: канвас Deep Void + рассеянное фиолетовое гало сверху.
// Гало едва заметное (атмосфера, не декорация) - по референсу GitHub и anti-slop правилам.
export function glowBackground() {
  return `radial-gradient(1000px circle at 50% -20%, rgba(167,162,255,0.14), transparent 55%), ${T.canvas}`;
}

// Glass-поверхность: translucent заливка + hairline. Без теней (elevation через прозрачность).
function surfaceSx() {
  return { backgroundColor: T.glass, border: `1px solid ${T.glassBorder}` };
}

// Лого-марка Admetrix: белый сквиркл, тёмная «A», зелёная перекладина -
// единственный акцент внутри буквы. Совпадает с favicon/logo192/logo512 в public/.
export function LogoMark({ size = 36 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0, display: 'block' }}>
      <rect width="64" height="64" rx="16" fill="#ffffff" />
      <path d="M15 47 L32 17 L49 47" stroke="#0d1117" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M24 36 H40" stroke="#26a144" strokeWidth="5.5" strokeLinecap="round" fill="none" />
    </svg>
  );
}

// Eyebrow-метка: mono-капс + затухающая линия (технический «штамп» раздела).
export function Eyebrow({ children, sx }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, ...sx }}>
      <Typography sx={{
        fontFamily: '"JetBrains Mono", ui-monospace, monospace',
        fontSize: 11, fontWeight: 500, letterSpacing: '0.16em',
        textTransform: 'uppercase', color: T.textSecondary, whiteSpace: 'nowrap',
      }}>
        {children}
      </Typography>
      <Box sx={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, rgba(255,255,255,0.14), transparent)' }} />
    </Box>
  );
}

export function Page({ title, subtitle, eyebrow, action, children }) {
  return (
    <Box sx={{ p: { xs: 2, md: 3.5 }, maxWidth: 1320, mx: 'auto' }}>
      {(title || action) && (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
            {title && (
              <Typography sx={{
                fontSize: 27, fontWeight: 600, color: 'text.primary',
                letterSpacing: '-0.03em', lineHeight: 1.2,
              }}>
                {title}
              </Typography>
            )}
            {subtitle && <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5 }}>{subtitle}</Typography>}
          </Box>
          {action}
        </Box>
      )}
      {children}
    </Box>
  );
}

// Без градиентного текста (anti-slop) - сплошной цвет заголовка.
export function GradientText({ children, sx }) {
  return (
    <Box component="span" sx={{ color: T.textPrimary, ...sx }}>
      {children}
    </Box>
  );
}

export function Card({ children, sx, ...rest }) {
  return (
    <Paper
      sx={[() => ({ ...surfaceSx(), p: 3, borderRadius: '16px' }), ...(Array.isArray(sx) ? sx : [sx])]}
      {...rest}
    >
      {children}
    </Paper>
  );
}

export function StatCard({ icon, value, label, sub, loading, delta, featured }) {
  const hasDelta = typeof delta === 'number';
  const up = hasDelta && delta >= 0;
  return (
    <Paper
      sx={[() => ({
        ...surfaceSx(), p: 2.5, borderRadius: '16px',
        // выделенная карточка - фиолетовый бордер (единственная роль violet)
        ...(featured ? { borderColor: 'rgba(140,147,251,0.55)' } : null),
        transition: 'background-color .2s ease',
        '&:hover': { backgroundColor: T.glassHover },
      })]}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: '10px', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: 'rgba(255,255,255,0.06)', border: `1px solid ${T.glassBorder}`,
        }}>
          {icon && React.cloneElement(icon, { sx: { fontSize: 20, color: T.textSecondary } })}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 25, fontWeight: 600, color: 'text.primary', lineHeight: 1.1, letterSpacing: '-0.025em' }}>
              {loading ? '-' : value}
            </Typography>
            {hasDelta && !loading && (
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: up ? 'success.main' : 'error.main' }}>
                {up ? '+' : '-'}{Math.abs(delta)}%
              </Typography>
            )}
          </Box>
          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.primary', mt: 0.5 }}>{label}</Typography>
          {sub && <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.15 }}>{sub}</Typography>}
        </Box>
      </Box>
    </Paper>
  );
}
